// frontend/src/pages/AdminPanel.jsx
// ✅ Style submission review admin panel
// Route: /admin (outside RequireAuth — uses own auth)

import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../lib/api.js";

/* ── Constants ───────────────────────────────────────────── */
const ADMIN_UID = process.env.REACT_APP_ADMIN_UID || "";

/* ── Styles ──────────────────────────────────────────────── */
const css = {
  // Layout
  page: {
    minHeight: "100vh",
    background: "#0f0f13",
    color: "#e8e6f0",
    fontFamily: "'DM Mono', 'Fira Code', monospace",
  },
  header: {
    background: "#1a1824",
    borderBottom: "1px solid #2d2b3d",
    padding: "0 32px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#a78bfa",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  body: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "32px 24px",
  },

  // Login
  loginWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#0f0f13",
  },
  loginBox: {
    background: "#1a1824",
    border: "1px solid #2d2b3d",
    borderRadius: 16,
    padding: "40px 36px",
    width: 360,
  },
  loginTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#a78bfa",
    marginBottom: 24,
    textAlign: "center",
    letterSpacing: "0.05em",
  },
  input: {
    width: "100%",
    background: "#0f0f13",
    border: "1px solid #2d2b3d",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#e8e6f0",
    fontSize: 13,
    fontFamily: "inherit",
    marginBottom: 12,
    boxSizing: "border-box",
    outline: "none",
  },
  loginBtn: {
    width: "100%",
    background: "#6d5ef9",
    border: "none",
    borderRadius: 8,
    padding: "11px 0",
    color: "white",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: 4,
  },

  // Stats bar
  statsBar: {
    display: "flex",
    gap: 16,
    marginBottom: 28,
    flexWrap: "wrap",
  },
  statCard: (color) => ({
    background: "#1a1824",
    border: `1px solid ${color}33`,
    borderRadius: 10,
    padding: "14px 20px",
    minWidth: 120,
    flex: 1,
  }),
  statNum: (color) => ({
    fontSize: 28,
    fontWeight: 700,
    color,
    lineHeight: 1,
  }),
  statLabel: {
    fontSize: 11,
    color: "#6b6880",
    marginTop: 4,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  // Tabs
  tabs: {
    display: "flex",
    gap: 4,
    marginBottom: 24,
    background: "#1a1824",
    borderRadius: 10,
    padding: 4,
    width: "fit-content",
  },
  tab: (active) => ({
    padding: "7px 18px",
    borderRadius: 7,
    border: "none",
    background: active ? "#6d5ef9" : "transparent",
    color: active ? "white" : "#6b6880",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    fontFamily: "inherit",
    transition: "all 0.15s",
  }),

  // Submission list
  card: {
    background: "#1a1824",
    border: "1px solid #2d2b3d",
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 10,
    cursor: "pointer",
    transition: "border-color 0.15s",
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  cardMeta: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    fontSize: 15,
    fontWeight: 600,
    color: "#e8e6f0",
    marginBottom: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardSub: {
    fontSize: 12,
    color: "#6b6880",
  },
  badge: (status) => {
    const map = {
      pending: { bg: "#f59e0b22", color: "#f59e0b", label: "PENDING" },
      approved: { bg: "#10b98122", color: "#10b981", label: "APPROVED" },
      rejected: { bg: "#ef444422", color: "#ef4444", label: "REJECTED" },
      in_review: { bg: "#6d5ef922", color: "#a78bfa", label: "IN REVIEW" },
    };
    const s = map[status] || map.pending;
    return {
      background: s.bg,
      color: s.color,
      borderRadius: 6,
      padding: "3px 8px",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      whiteSpace: "nowrap",
    };
  },

  // Review panel
  reviewPanel: {
    display: "grid",
    gridTemplateColumns: "1fr 340px",
    gap: 20,
    alignItems: "start",
  },
  reviewMain: {
    background: "#1a1824",
    border: "1px solid #2d2b3d",
    borderRadius: 12,
    overflow: "hidden",
  },
  reviewSidebar: {
    background: "#1a1824",
    border: "1px solid #2d2b3d",
    borderRadius: 12,
    padding: 20,
    position: "sticky",
    top: 76,
  },
  reviewHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #2d2b3d",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  backBtn: {
    background: "none",
    border: "1px solid #2d2b3d",
    borderRadius: 6,
    color: "#a78bfa",
    cursor: "pointer",
    padding: "5px 10px",
    fontSize: 13,
    fontFamily: "inherit",
  },
  imageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: 8,
    padding: 16,
  },
  imgWrap: (selected) => ({
    position: "relative",
    borderRadius: 8,
    overflow: "hidden",
    border: selected ? "2px solid #6d5ef9" : "2px solid transparent",
    cursor: "pointer",
    transition: "border-color 0.15s",
    aspectRatio: "1",
    background: "#0f0f13",
  }),
  imgCheck: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    background: "#6d5ef9",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    color: "white",
    fontWeight: 700,
  },
  sidebarLabel: {
    fontSize: 11,
    color: "#6b6880",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 6,
    marginTop: 16,
  },
  sidebarValue: {
    fontSize: 13,
    color: "#e8e6f0",
    lineHeight: 1.6,
  },
  textarea: {
    width: "100%",
    background: "#0f0f13",
    border: "1px solid #2d2b3d",
    borderRadius: 8,
    padding: "10px 12px",
    color: "#e8e6f0",
    fontSize: 12,
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: 80,
    boxSizing: "border-box",
    outline: "none",
    marginTop: 4,
  },
  approveBtn: {
    width: "100%",
    background: "#10b981",
    border: "none",
    borderRadius: 8,
    padding: "10px 0",
    color: "white",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: 8,
    transition: "opacity 0.15s",
  },
  rejectBtn: {
    width: "100%",
    background: "#ef4444",
    border: "none",
    borderRadius: 8,
    padding: "10px 0",
    color: "white",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: 8,
    transition: "opacity 0.15s",
  },
  saveBtn: {
    width: "100%",
    background: "transparent",
    border: "1px solid #2d2b3d",
    borderRadius: 8,
    padding: "9px 0",
    color: "#a78bfa",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: 8,
  },
  uploadBtn: {
    background: "#6d5ef9",
    border: "none",
    borderRadius: 8,
    padding: "9px 16px",
    color: "white",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: 8,
  },
  selectionBar: {
    padding: "10px 16px",
    borderTop: "1px solid #2d2b3d",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#0f0f13",
    fontSize: 12,
    color: "#6b6880",
  },
  toast: (type) => ({
    position: "fixed",
    bottom: 24,
    right: 24,
    background: type === "success" ? "#10b981" : type === "error" ? "#ef4444" : "#6d5ef9",
    color: "white",
    padding: "12px 20px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    zIndex: 999,
    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
    fontFamily: "inherit",
  }),
};

/* ── Main Component ──────────────────────────────────────── */
export default function AdminPanel() {
  const [auth, setAuth] = useState(() => {
    try {
      const s = sessionStorage.getItem("artibytes_admin");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });

  if (!auth) return <LoginScreen onLogin={setAuth} />;

  return (
    <div style={css.page}>
      <header style={css.header}>
        <span style={css.headerTitle}>⬡ ArtiBytes Admin</span>
        <span style={{ fontSize: 12, color: "#6b6880" }}>
          {auth.uid} &nbsp;
          <button
            onClick={() => { sessionStorage.removeItem("artibytes_admin"); setAuth(null); }}
            style={{ background: "none", border: "none", color: "#6b6880", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}
          >
            sign out
          </button>
        </span>
      </header>
      <Dashboard auth={auth} />
    </div>
  );
}

/* ── Login ───────────────────────────────────────────────── */
function LoginScreen({ onLogin }) {
  const [uid, setUid] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/stats`, {
        headers: {
          "x-admin-secret": secret,
          "x-pi-uid": uid,
        },
      });
      if (res.ok) {
        const authData = { uid, secret };
        sessionStorage.setItem("artibytes_admin", JSON.stringify(authData));
        onLogin(authData);
      } else {
        setError("Invalid credentials");
      }
    } catch {
      setError("Connection failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={css.loginWrap}>
      <div style={css.loginBox}>
        <div style={css.loginTitle}>⬡ Admin Access</div>
        <form onSubmit={handleLogin}>
          <input
            style={css.input}
            placeholder="Pi UID"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            autoComplete="off"
          />
          <input
            style={css.input}
            type="password"
            placeholder="Admin Secret"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="off"
          />
          {error && <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 8 }}>{error}</div>}
          <button style={css.loginBtn} type="submit" disabled={loading}>
            {loading ? "Verifying..." : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Dashboard ───────────────────────────────────────────── */
function Dashboard({ auth }) {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");
  const [submissions, setSubmissions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [selected, setSelected] = useState(null); // jobId being reviewed
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = { "x-admin-secret": auth.secret, "x-pi-uid": auth.uid };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const fetchSubmissions = useCallback(async (status = tab) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions?status=${status}&limit=30`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.items || []);
        setPagination(data.pagination);
      }
    } catch {}
    setLoading(false);
  }, [tab]);

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchSubmissions(tab); setSelected(null); }, [tab]);

  const handleReviewDone = () => {
    fetchStats();
    fetchSubmissions(tab);
    setSelected(null);
  };

  if (selected) {
    return (
      <div style={css.body}>
        <ReviewPanel
          jobId={selected}
          auth={auth}
          onBack={() => setSelected(null)}
          onDone={(msg, type) => { showToast(msg, type); handleReviewDone(); }}
        />
        {toast && <div style={css.toast(toast.type)}>{toast.msg}</div>}
      </div>
    );
  }

  return (
    <div style={css.body}>
      {/* Stats */}
      {stats && (
        <div style={css.statsBar}>
          <div style={css.statCard("#f59e0b")}>
            <div style={css.statNum("#f59e0b")}>{stats.pending}</div>
            <div style={css.statLabel}>Pending</div>
          </div>
          <div style={css.statCard("#10b981")}>
            <div style={css.statNum("#10b981")}>{stats.approved}</div>
            <div style={css.statLabel}>Approved</div>
          </div>
          <div style={css.statCard("#ef4444")}>
            <div style={css.statNum("#ef4444")}>{stats.rejected}</div>
            <div style={css.statLabel}>Rejected</div>
          </div>
          <div style={css.statCard("#a78bfa")}>
            <div style={css.statNum("#a78bfa")}>{stats.total}</div>
            <div style={css.statLabel}>Total</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={css.tabs}>
        {["pending", "approved", "rejected", "all"].map((t) => (
          <button key={t} style={css.tab(tab === t)} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ color: "#6b6880", padding: "40px 0", textAlign: "center" }}>Loading...</div>
      ) : submissions.length === 0 ? (
        <div style={{ color: "#6b6880", padding: "40px 0", textAlign: "center" }}>
          No {tab} submissions
        </div>
      ) : (
        submissions.map((s) => (
          <div
            key={s.id}
            style={css.card}
            onClick={() => setSelected(s.id)}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = "#6d5ef9"}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = "#2d2b3d"}
          >
            <div style={css.cardMeta}>
              <div style={css.cardName}>{s.styleName || "Untitled Style"}</div>
              <div style={css.cardSub}>
                {s.username || s.uid} &nbsp;·&nbsp; {s.imageCount} images &nbsp;·&nbsp;{" "}
                {new Date(s.createdAt).toLocaleDateString()}
              </div>
            </div>
            <div style={css.badge(s.reviewStatus || s.status)}>
              {(s.reviewStatus || s.status).toUpperCase()}
            </div>
            <div style={{ color: "#a78bfa", fontSize: 18 }}>→</div>
          </div>
        ))
      )}

      {toast && <div style={css.toast(toast.type)}>{toast.msg}</div>}
    </div>
  );
}

/* ── Review Panel ────────────────────────────────────────── */
function ReviewPanel({ jobId, auth, onBack, onDone }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [recipeNotes, setRecipeNotes] = useState("");
  const [styleRecipe, setStyleRecipe] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);

  const headers = {
    "x-admin-secret": auth.secret,
    "x-pi-uid": auth.uid,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/admin/submissions/${jobId}`, {
          headers: { "x-admin-secret": auth.secret, "x-pi-uid": auth.uid },
        });
        if (res.ok) {
          const data = await res.json();
          setJob(data);
          setRecipeNotes(data.recipeNotes || "");
          setStyleRecipe(data.styleRecipe ? JSON.stringify(data.styleRecipe, null, 2) : "");
          // Pre-select already-uploaded images
          if (data.selectedImages?.length > 0) {
            setSelectedIndices(new Set(data.selectedImages.map((img) => img.index)));
          }
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [jobId]);

  const toggleImage = (idx) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = () => setSelectedIndices(new Set(job.images.map((_, i) => i)));
  const selectNone = () => setSelectedIndices(new Set());

  const handleUploadSelected = async () => {
    if (selectedIndices.size === 0) return;
    setUploading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${jobId}/upload-selected`, {
        method: "POST",
        headers,
        body: JSON.stringify({ selectedIndices: Array.from(selectedIndices) }),
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      alert(`✅ Uploaded ${data.uploadedCount} images to GCS`);
    } catch (err) {
      alert("❌ Upload failed: " + err.message);
    }
    setUploading(false);
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      let recipeObj = null;
      if (styleRecipe.trim()) {
        try { recipeObj = JSON.parse(styleRecipe); } catch { recipeObj = styleRecipe; }
      }
      await fetch(`${API_BASE}/api/admin/submissions/${jobId}/notes`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ recipeNotes, styleRecipe: recipeObj }),
      });
    } catch {}
    setSaving(false);
  };

  const handleApprove = async () => {
    setActing(true);
    try {
      let recipeObj = null;
      if (styleRecipe.trim()) {
        try { recipeObj = JSON.parse(styleRecipe); } catch { recipeObj = styleRecipe; }
      }
      const res = await fetch(`${API_BASE}/api/admin/submissions/${jobId}/approve`, {
        method: "POST",
        headers,
        body: JSON.stringify({ recipeNotes, styleRecipe: recipeObj }),
      });
      if (res.ok) {
        onDone(`✅ "${job.styleName}" approved — user notified`, "success");
      } else {
        onDone("Approval failed", "error");
      }
    } catch {
      onDone("Approval failed", "error");
    }
    setActing(false);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    setActing(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${jobId}/reject`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reason: rejectionReason }),
      });
      if (res.ok) {
        onDone(`❌ "${job.styleName}" rejected — user notified`, "error");
      } else {
        onDone("Rejection failed", "error");
      }
    } catch {
      onDone("Rejection failed", "error");
    }
    setActing(false);
  };

  if (loading) {
    return (
      <div style={{ color: "#6b6880", padding: "60px 0", textAlign: "center" }}>
        Loading submission + extracting images...
      </div>
    );
  }

  if (!job) {
    return <div style={{ color: "#ef4444", padding: 24 }}>Submission not found</div>;
  }

  const isDone = job.reviewStatus === "approved" || job.reviewStatus === "rejected";

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button style={css.backBtn} onClick={onBack}>← Back</button>
        <div>
          <span style={{ fontSize: 17, fontWeight: 700, color: "#e8e6f0" }}>
            {job.styleName || "Untitled Style"}
          </span>
          <span style={{ marginLeft: 10 }}>
            <span style={css.badge(job.reviewStatus || job.status)}>
              {(job.reviewStatus || job.status).toUpperCase()}
            </span>
          </span>
        </div>
      </div>

      <div style={css.reviewPanel}>
        {/* Main: Image grid */}
        <div style={css.reviewMain}>
          <div style={css.reviewHeader}>
            <span style={{ fontSize: 13, color: "#a78bfa", fontWeight: 600 }}>
              {job.images?.length || 0} images from ZIP
            </span>
            <span style={{ fontSize: 12, color: "#6b6880", marginLeft: "auto" }}>
              {selectedIndices.size} selected
            </span>
            {!isDone && (
              <>
                <button
                  onClick={selectAll}
                  style={{ ...css.backBtn, marginLeft: 8 }}
                >
                  All
                </button>
                <button onClick={selectNone} style={css.backBtn}>None</button>
              </>
            )}
          </div>

          {job.images?.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#6b6880", fontSize: 13 }}>
              No images could be extracted from the ZIP
            </div>
          ) : (
            <div style={css.imageGrid}>
              {job.images.map((img, i) => (
                <div
                  key={i}
                  style={css.imgWrap(selectedIndices.has(i))}
                  onClick={() => !isDone && toggleImage(i)}
                >
                  <img
                    src={img.signedUrl}
                    alt={img.filename}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    loading="lazy"
                  />
                  {selectedIndices.has(i) && (
                    <div style={css.imgCheck}>✓</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Selection action bar */}
          {!isDone && (
            <div style={css.selectionBar}>
              <span>{selectedIndices.size} of {job.images?.length || 0} selected for training</span>
              <button
                style={{ ...css.uploadBtn, opacity: selectedIndices.size === 0 || uploading ? 0.5 : 1 }}
                onClick={handleUploadSelected}
                disabled={selectedIndices.size === 0 || uploading}
              >
                {uploading ? "Uploading..." : `Upload ${selectedIndices.size} to GCS`}
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={css.reviewSidebar}>
          {/* Creator info */}
          <div style={css.sidebarLabel}>Submitted by</div>
          <div style={css.sidebarValue}>{job.username || job.uid}</div>

          <div style={css.sidebarLabel}>Submitted</div>
          <div style={css.sidebarValue}>
            {new Date(job.createdAt).toLocaleString()}
          </div>

          <div style={css.sidebarLabel}>Style description</div>
          <div style={{ ...css.sidebarValue, maxHeight: 120, overflowY: "auto", fontSize: 12, color: "#9ca3af", lineHeight: 1.6 }}>
            {job.styleDescription || <span style={{ color: "#4b4b60" }}>No description provided</span>}
          </div>

          {/* Recipe notes */}
          {!isDone && (
            <>
              <div style={css.sidebarLabel}>Recipe notes (private)</div>
              <textarea
                style={css.textarea}
                placeholder="Your notes on the style recipe..."
                value={recipeNotes}
                onChange={(e) => setRecipeNotes(e.target.value)}
                rows={4}
              />

              <div style={css.sidebarLabel}>Style recipe JSON (optional)</div>
              <textarea
                style={{ ...css.textarea, fontFamily: "'DM Mono', monospace", fontSize: 11 }}
                placeholder={'{\n  "preset": "...",\n  "strength": 0.8\n}'}
                value={styleRecipe}
                onChange={(e) => setStyleRecipe(e.target.value)}
                rows={5}
              />

              <button
                style={{ ...css.saveBtn, opacity: saving ? 0.5 : 1 }}
                onClick={handleSaveNotes}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save notes"}
              </button>

              <div style={{ borderTop: "1px solid #2d2b3d", margin: "16px 0" }} />

              {/* Actions */}
              {!showRejectForm ? (
                <>
                  <button
                    style={{ ...css.approveBtn, opacity: acting ? 0.5 : 1 }}
                    onClick={handleApprove}
                    disabled={acting}
                  >
                    ✅ Approve + Notify User
                  </button>
                  <button
                    style={{ ...css.rejectBtn, opacity: acting ? 0.5 : 1 }}
                    onClick={() => setShowRejectForm(true)}
                    disabled={acting}
                  >
                    ❌ Reject
                  </button>
                </>
              ) : (
                <>
                  <div style={css.sidebarLabel}>Rejection reason (sent to user)</div>
                  <textarea
                    style={css.textarea}
                    placeholder="Explain why the submission was rejected..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <button
                    style={{ ...css.rejectBtn, opacity: !rejectionReason.trim() || acting ? 0.5 : 1 }}
                    onClick={handleReject}
                    disabled={!rejectionReason.trim() || acting}
                  >
                    {acting ? "Rejecting..." : "Confirm Rejection"}
                  </button>
                  <button
                    style={css.saveBtn}
                    onClick={() => setShowRejectForm(false)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </>
          )}

          {/* Read-only view for done submissions */}
          {isDone && (
            <>
              {job.recipeNotes && (
                <>
                  <div style={css.sidebarLabel}>Recipe notes</div>
                  <div style={{ ...css.sidebarValue, fontSize: 12, color: "#9ca3af" }}>
                    {job.recipeNotes}
                  </div>
                </>
              )}
              {job.rejectionReason && (
                <>
                  <div style={css.sidebarLabel}>Rejection reason</div>
                  <div style={{ ...css.sidebarValue, fontSize: 12, color: "#ef4444" }}>
                    {job.rejectionReason}
                  </div>
                </>
              )}
              {job.reviewedAt && (
                <>
                  <div style={css.sidebarLabel}>Reviewed</div>
                  <div style={css.sidebarValue}>
                    {new Date(job.reviewedAt).toLocaleString()}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}